import { useCallback, useContext, useMemo } from 'react';

import type { ConfigContextValue } from '@core/context';
import { BaseUrlContext, ConfigContext } from '@core/context';
import { useProjectStore } from '@core/store';

/**
 * Returns a function that generates a dash link url for the current project.
 * When invoked, the function will return a url base path for either
 * the project's dash or the enterprise group dash that the project belongs to.
 *
 * @example
 * const dashLinkUrl = useDashLinkUrl();
 * dashLinkUrl('appearance') // => /my-project/v2.0/dash?to=appearance
 * dashLinkUrl('translation', { toEnterpriseGroup: true }) // => /group/my-enterprise/translation
 * dashLinkUrl('settings', { toEnterpriseGroup: true, fromEnterpriseGroup: true }) // => /group/my-enterprise/settings
 */
export default function useDashLinkUrl() {
  const { domainFull: dashUrl } = useContext(ConfigContext) as ConfigContextValue;
  const baseUrl = useContext(BaseUrlContext);
  const [parentSubdomain, projectSubdomain] = useProjectStore(s => [s.data.parent, s.data.subdomain]);
  const projectBaseUrl = useMemo(() => (baseUrl === '/' ? '' : baseUrl), [baseUrl]);

  const func = useCallback(
    (to: string, { toEnterpriseGroup = false, fromEnterpriseGroup = false } = {}) => {
      if (toEnterpriseGroup) {
        if (fromEnterpriseGroup) {
          return `${dashUrl}/group/${projectSubdomain}/${to}`;
        } else if (!parentSubdomain) {
          return '';
        }
        return `${dashUrl}/group/${parentSubdomain}/${to}`;
      }
      return `${projectBaseUrl}/dash?to=${to}`;
    },
    [dashUrl, parentSubdomain, projectBaseUrl, projectSubdomain],
  );

  return func;
}
